import type { Ref } from 'vue';
import type { Axios } from 'axios';
import { TommyGateway } from '@/widgets/Wizard/gateways/tommy';
import type {
    AccommodationExtra,
    AccommodationLocation,
    ArrangementCalendarInfo,
    AvailableAccommodation,
    ExistingBooking,
    MadeBooking,
    MetaData,
    Payment,
    PriceCalculation,
    Transaction,
} from '@/widgets/Wizard/models/wizard';
import {
    parseNewBooking,
    parseCountriesResults,
    parseItemsResult,
    parseMetaData,
    parseOffer,
    parsePriceCalculation,
    parseSearchResults,
    parseBooking,
    parseTransaction,
    parsePayment,
} from '@/widgets/Wizard/gateways/tommy3/api/conversion';
import { tommyDate } from '@/widgets/Wizard/modules/tommy3/data-conversion';
import type { FormConfigField } from '@/common/store/BookStore';
import moment from '@/common/modules/moment-fallback';

export class Tommy3Service extends TommyGateway {
    constructor(
        private readonly operationalClient: Axios,
        private readonly widgetClient: Axios,
    ) {
        super();
    }

    public async getMetaData(salesChannel?: number): Promise<MetaData | null> {
        const queryParams: any = {};

        if (salesChannel) {
            queryParams['sales-channel'] = salesChannel;
        }

        try {
            const response = await this.widgetClient.get('/widget/metadata', { params: queryParams });

            if (response.status !== 200 || !response.data) {
                return null;
            }

            const metaData = response.data;

            if (!metaData.success || !metaData.data) {
                return null;
            }

            return parseMetaData(metaData.data);
        } catch (err) {
            console.warn('Tommy 3 call failed!', err);

            return null;
        }
    }

    public async getCountries(locale: string): Promise<{ code: string; name: string }[] | null> {
        try {
            const response = await this.widgetClient.get('/widget/country', { params: { language: locale } });

            if (response.status !== 200 || !response.data) {
                return null;
            }

            const countries = response.data;

            if (!countries.success || !countries.data) {
                return null;
            }

            return parseCountriesResults(countries.data);
        } catch (err) {
            console.warn('Tommy 3 call failed!', err);

            return null;
        }
    }

    public async checkAvailability(
        arrivalDate: string,
        departureDate: string,
        amenities: { id: string }[],
        ageCategories?: { [id: string]: number },
        salesChannel?: number,
    ): Promise<AvailableAccommodation[] | null> {
        const queryParams: any = {
            'date-from': arrivalDate,
            'date-till': departureDate,
            'age-category': this.parseAgeCategories(ageCategories),
        };

        if (salesChannel) {
            queryParams['sales-channel'] = salesChannel;
        }

        if (amenities.length > 0) {
            queryParams['amenities'] = JSON.stringify(amenities);
        }

        if (queryParams['age-category']) {
            queryParams['age-category'] = JSON.stringify(queryParams['age-category']);
        }

        try {
            const response = await this.widgetClient.get('/widget/search', { params: queryParams });

            if (response.status !== 200 || !response.data) {
                return null;
            }

            const searchResults = response.data;

            if (!searchResults.success || !searchResults.data) {
                return null;
            }

            return parseSearchResults(searchResults.data);
        } catch (err) {
            console.warn('Tommy 3 call failed!', err);

            return null;
        }
    }

    public async getAccommodationLocations(
        arrivalDate: Date,
        departureDate: Date,
        accommodationId: number,
        amenities: { id: string }[],
        ageCategories?: { [id: string]: number },
        salesChannel?: number,
    ): Promise<AccommodationLocation[] | null> {
        const queryParams: any = {
            'arrival-date': tommyDate(arrivalDate),
            'departure-date': tommyDate(departureDate),
            accommodation: accommodationId,
            'age-category': this.parseAgeCategories(ageCategories),
        };

        if (queryParams['age-category']) {
            queryParams['age-category'] = JSON.stringify(queryParams['age-category']);
        }

        if (amenities.length > 0) {
            queryParams['amenities'] = JSON.stringify(amenities);
        }

        if (salesChannel) {
            queryParams['sales-channel'] = salesChannel;
        }

        try {
            const response = await this.widgetClient.get('/widget/offers', { params: queryParams });

            if (response.status !== 200 || !response.data) {
                return null;
            }

            const locations = response.data;

            if (!locations.success || !locations.data) {
                return null;
            }

            return parseOffer(locations.data);
        } catch (err) {
            console.warn('Tommy 3 call failed!', err);

            return null;
        }
    }

    public async getAccommodationBookingExtras(
        arrivalDate: Date,
        departureDate: Date,
        accommodationId: number,
        amenities: { id: string }[],
        ageCategories?: { [id: string]: number },
        locationId?: number,
        salesChannel?: number,
    ): Promise<AccommodationExtra[] | null> {
        const queryParams: any = {
            'arrival-date': tommyDate(arrivalDate),
            'departure-date': tommyDate(departureDate),
            accommodation: accommodationId,
            'age-category': this.parseAgeCategories(ageCategories),
        };

        if (queryParams['age-category']) {
            queryParams['age-category'] = JSON.stringify(queryParams['age-category']);
        }

        if (amenities.length > 0) {
            queryParams['amenities'] = JSON.stringify(amenities);
        }

        if (locationId) {
            queryParams['location'] = locationId;
        }

        if (salesChannel) {
            queryParams['sales-channel'] = salesChannel;
        }

        try {
            const response = await this.widgetClient.get('/widget/items', { params: queryParams });

            if (response.status !== 200 || !response.data) {
                return null;
            }

            const itemResults = response.data;

            if (!itemResults.success || !itemResults.data) {
                return null;
            }

            return parseItemsResult(itemResults.data);
        } catch (err) {
            console.warn('Tommy 3 call failed!', err);

            return null;
        }
    }

    public async getArrangementCalendarInfo(arrangementIds: string[]): Promise<ArrangementCalendarInfo[] | null> {
        if (arrangementIds.length === 0) {
            return null;
        }

        try {
            const response = await this.widgetClient.get('/widget/arrangement-calendar-info', {
                params: {
                    arrangement: arrangementIds.length === 1 ? arrangementIds[0] : arrangementIds,
                },
            });

            if (response.status !== 200 || !response.data) {
                return null;
            }

            if (!response.data.success || !response.data.data) {
                return null;
            }

            const rawData = response.data.data;
            const items = Array.isArray(rawData) ? rawData : [rawData];
            const results: ArrangementCalendarInfo[] = [];

            for (const item of items) {
                results.push({
                    id: `${item.id}`,
                    accommodationId: item['accommodation-id'],
                    dateFrom: item['date-from'],
                    dateTill: item['date-till'],
                    duration: item.duration,
                });
            }

            return results;
        } catch (err) {
            console.warn('Tommy 3 call failed!', err);

            return null;
        }
    }

    public async getPriceCalculation(
        locale: string,
        arrivalDate: Date,
        departureDate: Date,
        accommodationId: number,
        amenities: { id: string }[],
        ageCategories?: { [id: string]: number },
        locationId?: number,
        extras?: { [id: string]: number },
        salesChannel?: number,
        arrangements?: { id: number | string }[],
    ): Promise<PriceCalculation | null> {
        const queryParams: any = {
            language: locale,
            'arrival-date': tommyDate(arrivalDate),
            'departure-date': tommyDate(departureDate),
            accommodation: accommodationId,
            'age-category': this.parseAgeCategories(ageCategories),
        };

        if (amenities.length > 0) {
            queryParams['amenities'] = JSON.stringify(amenities);
        }

        if (queryParams['age-category']) {
            queryParams['age-category'] = JSON.stringify(queryParams['age-category']);
        }

        if (locationId) {
            queryParams['location'] = locationId;
        }

        if (salesChannel) {
            queryParams['sales-channel'] = salesChannel;
        }

        if (arrangements && arrangements.length > 0) {
            queryParams['arrangements'] = JSON.stringify(arrangements);
        }

        if (extras && Object.keys(extras).length > 0) {
            queryParams['items'] = [];

            for (const itemId in extras) {
                const amount = extras[itemId];
                if (!amount || amount <= 0) {
                    continue;
                }

                queryParams['items'].push({
                    id: itemId,
                    amount,
                });
            }

            if (queryParams['items'].length > 0) {
                queryParams['items'] = JSON.stringify(queryParams['items']);
            } else {
                delete queryParams['items'];
            }
        }

        try {
            const response = await this.widgetClient.get('/widget/price', { params: queryParams });

            if (response.status !== 200 || !response.data) {
                return null;
            }

            const priceCalculation = response.data;

            if (!priceCalculation.success || !priceCalculation.data) {
                return null;
            }

            return parsePriceCalculation(priceCalculation.data);
        } catch (err) {
            console.warn('Tommy 3 call failed!', err);

            return null;
        }
    }

    public async handleDetailsChange(field: FormConfigField, modelData: Ref<any>): Promise<any> {
        if (
            'street-number' !== field.code &&
            'street-number-suffix' !== field.code &&
            'post-code' !== field.code &&
            'country' !== field.code
        ) {
            return Promise.resolve(true);
        }

        const zipCodeParams = {
            'post-code': modelData.value['post-code'],
            'street-number': modelData.value['street-number'],
            'street-number-suffix': modelData.value['street-number-suffix'],
        };

        if ('NL' === modelData.value.country && modelData.value['post-code']) {
            // await this.updateReceipt('post-code');
        }

        if (!zipCodeParams['post-code'] || !zipCodeParams['street-number']) {
            return Promise.resolve({ success: false, error: 'Ignored, no zipcode or housenumber filled', data: {} });
        }

        if ('NL' !== modelData.value.country) {
            return Promise.resolve({ success: false, error: 'Ignored, not NL', data: {} });
        }

        const zipcodeResponse = (
            await this.widgetClient.get('widget/address', { params: zipCodeParams }).catch((error: any) => {
                return { success: false, error, data: {} };
            })
        ).data;

        if (zipcodeResponse && zipcodeResponse.success && !!zipcodeResponse.data) {
            modelData.value['street'] = zipcodeResponse.data.street || '';
            modelData.value['street-number'] = zipcodeResponse.data['street-number'] || '';
            modelData.value['street-number-suffix'] = zipcodeResponse.data['street-number-suffix'] || '';
            modelData.value['post-code'] = zipcodeResponse.data['post-code'] || '';
            modelData.value['city'] = zipcodeResponse.data.city || '';
            modelData.value['country'] = 'NL';
        } else {
            console.error('Failed to fetch zipcode data', zipcodeResponse);

            return Promise.reject(zipcodeResponse);
        }

        return Promise.resolve(zipcodeResponse);
    }

    private parseAgeCategories(ageCategories?: { [id: string]: number }): { id: number; pax: number }[] | undefined {
        if (!ageCategories || Object.keys(ageCategories).length === 0) {
            return undefined;
        }

        const categories: { id: number; pax: number }[] = [];

        for (const ageCategoryId in ageCategories) {
            const amount = ageCategories[ageCategoryId];

            if (!amount || amount <= 0) {
                continue;
            }

            categories.push({ id: parseInt(ageCategoryId), pax: amount });
        }

        return categories;
    }

    private parseExtras(extras: { [id: string]: number } | undefined): { id: string; amount: number }[] {
        if (!extras) {
            return [];
        }

        const parsedExtras: { id: string; amount: number }[] = [];

        for (const itemId in extras) {
            const amount = extras[itemId];
            if (!amount || amount <= 0) {
                continue;
            }

            parsedExtras.push({
                id: itemId,
                amount,
            });
        }

        return parsedExtras;
    }

    public async book(
        arrivalDate: Date,
        departureDate: Date,
        accommodationId: number,
        extras: { [id: string]: number },
        ageCategories: { [id: string]: number },
        customerFields: { [field: string]: any },
        locale: string,
        locationId?: number,
        salesChannel?: number,
        arrangements?: { id: number | string }[],
    ): Promise<MadeBooking | null> {
        // Normalize customer fields
        const normalizedCustomerFields: any = {};

        for (const fieldId in customerFields) {
            let fieldValue = customerFields[fieldId];

            if (['birthdate', 'date-of-birth'].includes(fieldId) && customerFields[fieldId]) {
                fieldValue = moment(fieldValue, 'DD-MM-YYYY').format('YYYY-MM-DD');
            }

            normalizedCustomerFields[this.renameField(fieldId)] = fieldValue;
        }

        normalizedCustomerFields.language = locale;

        // Construct booking
        const booking: any = {
            'arrival-date': tommyDate(arrivalDate),
            'departure-date': tommyDate(departureDate),
            accommodation: accommodationId,
            'age-categories': this.parseAgeCategories(ageCategories),
            customer: normalizedCustomerFields,
            items: this.parseExtras(extras),
        };

        if (locationId) {
            booking['location'] = locationId;
        }

        if (salesChannel) {
            booking['sales-channel'] = salesChannel;
        }

        if (arrangements && arrangements.length > 0) {
            booking['arrangements'] = arrangements;
        }

        try {
            const response = await this.widgetClient.post('/widget/reservation', booking);

            if (response.status !== 200 || !response.data) {
                return null;
            }

            const bookingResponse = response.data;

            if (!bookingResponse.success || !bookingResponse.data) {
                return null;
            }

            return parseNewBooking(bookingResponse.data);
        } catch (err) {
            console.warn('Tommy 3 call failed!', err);

            return null;
        }
    }

    public async getBooking(reservationNumber: string): Promise<ExistingBooking | null> {
        try {
            const response = await this.operationalClient.get(
                `/operational/reservation/by-number/${reservationNumber}`,
            );

            if (response.status !== 200 || !response.data) {
                return null;
            }

            const bookingResponse = response.data;

            if (!bookingResponse.success || !bookingResponse.data) {
                return null;
            }

            return parseBooking(bookingResponse.data);
        } catch (err) {
            console.warn('Tommy 3 call failed!', err);

            return null;
        }
    }

    public async bookingPaid(bookingId: number): Promise<boolean | null> {
        try {
            const response = await this.operationalClient.get(`/operational/reservation/${bookingId}`);

            if (response.status !== 200 || !response.data) {
                return null;
            }

            const bookingResponse = response.data;

            if (!bookingResponse.success || !bookingResponse.data) {
                return null;
            }

            return bookingResponse.data.amountOpen === 0;
        } catch (err) {
            console.warn('Tommy 3 call failed!', err);

            return null;
        }
    }

    public async getPaymentForBooking(bookingId: number): Promise<Payment | null> {
        try {
            const response = await this.operationalClient.get(`/operational/reservation/${bookingId}/payment`);

            if (response.status !== 200 || !response.data) {
                return null;
            }

            const transaction = response.data;

            if (!transaction.success || !transaction.data) {
                return null;
            }

            return parsePayment(transaction.data);
        } catch (err) {
            console.warn('Tommy 3 call failed!', err);

            return null;
        }
    }

    public async startBookingPayment(bookingId: number): Promise<Transaction | null> {
        try {
            const response = await this.operationalClient.put(`/operational/reservation/${bookingId}/payment`);

            if (response.status !== 200 || !response.data) {
                return null;
            }

            const transaction = response.data;

            if (!transaction.success || !transaction.data) {
                return null;
            }

            return parseTransaction(transaction.data);
        } catch (err) {
            console.warn('Tommy 3 call failed!', err);

            return null;
        }
    }

    public async markBookingAsArrived(bookingId: number): Promise<boolean | null> {
        try {
            const response = await this.operationalClient.put(`/operational/reservation/${bookingId}/arrived`);

            if (response.status !== 200 || !response.data) {
                return null;
            }

            const arrived = response.data;

            return !!arrived.success;
        } catch (err) {
            console.warn('Tommy 3 call failed!', err);

            return null;
        }
    }

    private renameField(origName: string) {
        switch (origName) {
            case 'infix':
                return 'last-name-prefix';
            case 'name':
                return 'last-name';
            case 'street':
                return 'street';
            case 'street-number':
                return 'street-number';
            case 'street-number-suffix':
                return 'street-number-suffix';
            case 'post-code':
                return 'post-code';
            case 'phone-number':
                return 'phone-number';
            case 'license-plate':
                return 'license-plate';
            case 'license-plate2':
                return 'license-plate2';
            case 'date-of-birth':
                return 'date-of-birth';
            default:
                return origName;
        }
    }
}
